import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import TimelineIcon from '@mui/icons-material/Timeline';
import { AppDispatch, RootState } from '../../store';
import { loadUser } from '../auth/authSlice';
import { joinAudienceFunnelBeta } from './api';
import AudienceFunnelSubnav from './AudienceFunnelSubnav';

const benefits = [
  {
    icon: <PeopleAltOutlinedIcon color="primary" />,
    title: 'Público',
    description: 'Entenda quem são Campeões, Em atenção, Novos… e veja a lista.',
  },
  {
    icon: <FilterListIcon color="primary" />,
    title: 'Filtros',
    description: 'Crie grupos com respostas do formulário (ex.: Paróquia).',
  },
  {
    icon: <TimelineIcon color="primary" />,
    title: 'Comunicar',
    description: 'Envie e-mail ou WhatsApp personalizado para um segmento.',
  },
];

const NovidadesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const enabled = useSelector(
    (state: RootState) => state.auth.user?.parish?.features?.audienceFunnelBetaEnabled === true
  );
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoin = async () => {
    setJoining(true);
    setError(null);
    try {
      await joinAudienceFunnelBeta();
      await dispatch(loadUser());
      navigate('/dashboard/publico');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      const fromApi = ax.response?.data?.message?.trim();
      setError(fromApi || 'Não foi possível entrar no beta agora. Tente novamente.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {enabled ? <AudienceFunnelSubnav title="Visão geral" /> : null}

        <Stack direction="row" spacing={1} alignItems="center">
          <NewReleasesIcon color="primary" />
          <Typography variant="h4" fontWeight={800}>
            Novidades
          </Typography>
          <Chip label="Beta" size="small" color="secondary" />
        </Stack>

        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.background.paper} 55%)`,
          }}
        >
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Público inteligente: análise, segmentos e interação
          </Typography>
          <Typography color="text.secondary" paragraph>
            Segmente automaticamente quem está ativo ou em risco, exporte CSV, envie mensagem e
            restrinja ingressos por grupo — tudo no mesmo módulo beta.
          </Typography>

          {enabled ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" onClick={() => navigate('/dashboard/publico')}>
                Ir para Público
              </Button>
              <Button variant="outlined" onClick={() => navigate('/dashboard/funil')}>
                Comunicar com segmentos
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              size="large"
              disabled={joining}
              onClick={handleJoin}
              startIcon={joining ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {joining ? 'Entrando…' : 'Testar agora'}
            </Button>
          )}

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </Paper>

        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            O que vem no beta
          </Typography>
          <Stack spacing={2}>
            {benefits.map((item) => (
              <Paper
                key={item.title}
                sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {item.icon}
                  <Box>
                    <Typography fontWeight={700}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};

export default NovidadesPage;
